import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import {
  Alert,
  Card,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TwoWheelerOutlinedIcon from "@mui/icons-material/TwoWheelerOutlined";
import { ConfirmarDialog, FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";

const VACIA = { id: null, nombre: "", activa: true, requierechasis: false, cuentacontable: "" };

const filaEncabezadoSx = {
  "& td": {
    border: "none",
    borderBottom: `2px solid ${FP.color.border}`,
    bgcolor: FP.color.surfaceAlt,
    fontWeight: 700,
    fontSize: "0.75rem",
    color: FP.color.textSecondary,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
};

/** Catálogo de categorías de gasto. Se abre desde el informe de gastos. */
function CategoriasGasto({ onExit, puedeModificar = true, puedeBorrar = false }) {
  const { post } = useApi();
  const [categorias, setCategorias] = useState([]);
  // Solo en negocios con contabilidad: cuenta de cada categoría (débito del
  // asiento del gasto en la contabilización diaria).
  const [contabilidad, setContabilidad] = useState(false);
  const [cuentas, setCuentas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [edicion, setEdicion] = useState(null); // categoría en el diálogo
  const [aEliminar, setAEliminar] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    const { status, data } = await post("/api/gastos_categorias");
    setCargando(false);
    if (status === 200) {
      setCategorias(data?.categorias || []);
      setContabilidad(Boolean(data?.contabilidad));
      setCuentas(data?.cuentas || []);
      setError("");
    } else {
      setError(data?.Error || "No se pudieron leer las categorías");
    }
  }, [post]);

  useEffect(() => {
    cargar();
  }, []);

  async function guardar() {
    setGuardando(true);
    const { status, data } = await post("/api/gastos_categoria_guardar", {
      id: edicion.id,
      nombre: edicion.nombre,
      activa: edicion.activa ? "S" : "N",
      requierechasis: edicion.requierechasis ? "S" : "N",
      cuentacontable: edicion.cuentacontable || "",
    });
    setGuardando(false);
    if (status === 200) {
      setEdicion(null);
      cargar();
    } else {
      alert(data?.Error || "No se pudo guardar la categoría");
    }
  }

  async function eliminar() {
    setGuardando(true);
    const { status, data } = await post("/api/gastos_categoria_eliminar", { id: aEliminar.id });
    setGuardando(false);
    if (status === 200) {
      setAEliminar(null);
      cargar();
    } else {
      alert(data?.Error || "No se pudo eliminar la categoría");
    }
  }

  const original = edicion?.id ? categorias.find((c) => c.id === edicion.id) : null;
  const renombra =
    original && original.gastos > 0 && original.nombre.trim() !== (edicion?.nombre || "").trim();

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Gastos"
        subtitle="Categorías de gasto"
        cta={
          <Stack direction="row" spacing={1.2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onExit}
              sx={{
                borderColor: FP.color.border,
                color: FP.color.textSecondary,
                borderRadius: 1.4,
                height: 38,
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Volver
            </Button>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              disabled={!puedeModificar}
              onClick={() => setEdicion({ ...VACIA })}
              sx={{
                bgcolor: FP.color.navy,
                color: "#FFFFFF",
                borderRadius: 1.4,
                height: 38,
                boxShadow: "none",
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
              }}
            >
              Nueva categoría
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, maxWidth: 900, mx: "auto" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Card
          elevation={0}
          sx={{ borderRadius: 2.5, border: `1px solid ${FP.color.border}`, overflow: "hidden" }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableBody>
                <TableRow sx={filaEncabezadoSx}>
                  <TableCell>Categoría</TableCell>
                  {contabilidad && <TableCell>Cuenta contable</TableCell>}
                  <TableCell align="center">Chasis</TableCell>
                  <TableCell align="right">Gastos</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell />
                </TableRow>
                {!cargando && categorias.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={contabilidad ? 6 : 5} align="center" sx={{ py: 5, color: FP.color.textMuted }}>
                      No hay categorías.
                    </TableCell>
                  </TableRow>
                )}
                {categorias.map((c) => (
                  <TableRow key={c.id} hover sx={c.activa ? undefined : { opacity: 0.55 }}>
                    <TableCell sx={{ fontWeight: 600 }}>{c.nombre}</TableCell>
                    {contabilidad && (
                      <TableCell>
                        {c.cuentacontable ? (
                          `${c.cuentacontable} · ${c.cuentanombre || "cuenta no encontrada"}`
                        ) : (
                          <Chip
                            size="small"
                            label="Sin cuenta"
                            sx={{ fontWeight: 600, bgcolor: FP.color.dangerSoft, color: FP.color.danger }}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {c.requierechasis && (
                        <Tooltip title="Al registrar un gasto de esta categoría se pide el chasis">
                          <Chip
                            size="small"
                            icon={<TwoWheelerOutlinedIcon sx={{ fontSize: 16 }} />}
                            label="Requiere chasis"
                            sx={{ fontWeight: 600, bgcolor: FP.color.accentBlueSoft, color: FP.color.accentBlue }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">{c.gastos}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={c.activa ? "Activa" : "Inactiva"}
                        sx={{
                          fontWeight: 700,
                          bgcolor: c.activa ? FP.color.successSoft : FP.color.surfaceAlt,
                          color: c.activa ? FP.color.successDark : FP.color.textMuted,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Editar">
                        <span>
                          <IconButton size="small" disabled={!puedeModificar} onClick={() => setEdicion({ ...c })}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {puedeBorrar && (
                        <Tooltip title={c.gastos > 0 ? "Tiene gastos: desactívela en vez de eliminarla" : "Eliminar"}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={c.gastos > 0}
                              onClick={() => setAEliminar(c)}
                              sx={{ color: FP.color.danger }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      <ConfirmarDialog
        abierto={Boolean(edicion)}
        titulo={edicion?.id ? "Editar categoría" : "Nueva categoría"}
        textoConfirmar="Guardar"
        confirmarDeshabilitado={!(edicion?.nombre || "").trim()}
        cargando={guardando}
        onCerrar={() => setEdicion(null)}
        onConfirmar={guardar}
      >
        <TextField
          label="Nombre"
          fullWidth
          autoFocus
          value={edicion?.nombre || ""}
          onChange={(e) => setEdicion((c) => ({ ...c, nombre: e.target.value }))}
          inputProps={{ maxLength: 100 }}
          InputLabelProps={{ shrink: true }}
          sx={{ ...fpInputSx, mt: 1 }}
        />
        {renombra && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Los {original.gastos} gastos registrados como «{original.nombre}» pasarán al nombre nuevo.
          </Alert>
        )}
        {contabilidad && (
          <Autocomplete
            options={cuentas}
            value={cuentas.find((c) => c.cuenta === edicion?.cuentacontable) || null}
            onChange={(e, v) => setEdicion((c) => ({ ...c, cuentacontable: v ? v.cuenta : "" }))}
            getOptionLabel={(o) => `${o.cuenta} · ${o.nombre}`}
            isOptionEqualToValue={(o, v) => o.cuenta === v.cuenta}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cuenta contable"
                helperText="Cuenta que se debita al contabilizar los gastos de esta categoría"
                InputLabelProps={{ shrink: true }}
                sx={{ ...fpInputSx, mt: 2 }}
              />
            )}
          />
        )}
        <FormControlLabel
          sx={{ mt: 1.5, display: "flex" }}
          control={
            <Switch
              checked={Boolean(edicion?.requierechasis)}
              onChange={(e) => setEdicion((c) => ({ ...c, requierechasis: e.target.checked }))}
            />
          }
          label={
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "0.92rem" }}>Requiere chasis</Typography>
              <Typography sx={{ color: FP.color.textMuted, fontSize: "0.78rem" }}>
                El gasto se liga a un motor: suma en Taller y en los informes del chasis.
              </Typography>
            </Box>
          }
        />
        <FormControlLabel
          sx={{ mt: 1, display: "flex" }}
          control={
            <Switch
              checked={Boolean(edicion?.activa)}
              onChange={(e) => setEdicion((c) => ({ ...c, activa: e.target.checked }))}
            />
          }
          label={
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "0.92rem" }}>Activa</Typography>
              <Typography sx={{ color: FP.color.textMuted, fontSize: "0.78rem" }}>
                Inactiva no aparece al registrar gastos; los ya registrados no cambian.
              </Typography>
            </Box>
          }
        />
      </ConfirmarDialog>

      <ConfirmarDialog
        abierto={Boolean(aEliminar)}
        titulo="Eliminar categoría"
        mensaje={`¿Eliminar la categoría «${aEliminar?.nombre || ""}»? No tiene gastos registrados.`}
        textoConfirmar="Eliminar"
        destructivo
        cargando={guardando}
        onCerrar={() => setAEliminar(null)}
        onConfirmar={eliminar}
      />
    </Box>
  );
}

export default CategoriasGasto;
